import { useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { IS_OFERTA_CONFIRMED } from 'application/consts';
import { TARIFFS } from 'application/pages/constants';
import AnalyticsService, { CalledFromValues, EventNameAMP, ParamNameAMP } from 'application/services/AnalyticsService';
import CompanyUtils from 'application/utils/CompanyUtils';
import { ModalTypes } from 'application/providers/ModalProvider';
import { useAppDispatch } from 'application/providers/StoreProvider';
import { fetchAccountData } from 'application/store';

import {
  AuthCurrentUserQuery,
  AuthCurrentUserDocument,
  CompanyContractStatusEnum,
  UserCompanyPureType,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { NEW_OFERTA_LINK, NEW_POPULAR_OFERTA_LINK } from './OfertaModal/consts';

interface UseOfertaButtonViewModel {
  hasActiveOfferta: boolean;
  isOffertaChangeModalAvailable: boolean;
  ofertaOnClick: () => void;
}

interface Props {
  isOffertaChanged: boolean;
  openModal: (type: ModalTypes, props: {}) => void;
  closeModal: () => void;
}

const useOfertaButtonViewModel = ({ isOffertaChanged, openModal, closeModal }: Props): UseOfertaButtonViewModel => {
  const dispatch = useAppDispatch();

  const { data } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const currentCompany = data?.currentUserPureQuery?.mainCompany as UserCompanyPureType | undefined;
  const actualContract = currentCompany?.contracts?.find((c) => c.status === CompanyContractStatusEnum.Actual);

  const isOfertaConfirmed = localStorage.getItem(IS_OFERTA_CONFIRMED);
  const isPopularTariff = CompanyUtils.compareTariff(currentCompany?.pricePlan?.alias ?? '', TARIFFS.POPULAR);

  const isOffertaChangeModalAvailable = useMemo(
    () => isOffertaChanged && isOfertaConfirmed !== 'true',
    [isOffertaChanged, isOfertaConfirmed]
  );

  const hasActiveOfferta = currentCompany
    ? CompanyUtils.checkIsOffertaAvailable(currentCompany.contracts, currentCompany.country)
    : false;

  const onOffertaSubmit = async () => {
    window.open(isPopularTariff ? NEW_POPULAR_OFERTA_LINK : NEW_OFERTA_LINK, '_blank');
    localStorage.setItem(IS_OFERTA_CONFIRMED, 'true');
    closeModal();
  };

  const ofertaOnClick = () => {
    AnalyticsService.event(EventNameAMP.SHOW_OFERTA_PRESSED, {
      [ParamNameAMP.CALLED_FROM]: CalledFromValues.BILLING_PAGE,
    });

    if (isOffertaChangeModalAvailable) {
      openModal(ModalTypes.OFFERTA, {
        onSubmit: onOffertaSubmit,
        isPopularTariff,
      });
    } else {
      CompanyUtils.openOffertaByCompanyCountry(currentCompany!, actualContract!);
    }
  };

  useEffect(() => {
    dispatch(fetchAccountData());
  }, []);

  return {
    hasActiveOfferta,
    isOffertaChangeModalAvailable,
    ofertaOnClick,
  };
};

export default useOfertaButtonViewModel;
